import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "utils/dayjs"
import cn from "classnames"

import { Guid, IValidationRule } from "@/types"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import { Grid } from "components/Grid"
import CustomError from "models/CustomError"
import ValidationMessage from "components/ValidationMessage/ValidationMessage"
import { hydrateInvoicePositions } from "stores/invoicePositions"
import ReservationInvoiceInvoicePosition from "models/ReservationInvoiceInvoicePosition"
import PartialNoteInvoicePosition from "./PartialNoteInvoicePosition"
import ValidationService from "services/ValidationService"
import { handleCreditDebitNoteCreateError, OutgoingInvoiceType } from "models/OutgoingInvoice"
import useOutgoingInvoice from "hooks/useOutgoingInvoice"
import Spinner from "components/Spinner/Spinner"
import ErpInvoiceNumber from "components/ErpInvoice/ErpInvoiceNumber"
import TextSection from "components/TextSection/TextSection"
import Input from "components/Input/Input"
import { INPUT_MAX_LENGTH_LARGE, PARTIAL_CREDIT_NOTE_TEXT_LINE_BREAK_AFTER } from "config/constants"
import FormField from "components/FormField/FormField"
import useTenantCurrency from "hooks/useTenantCurrency"
import useExchangeRate from "hooks/useExchangeRate"
import Text from "components/Text/Text"
import { applyExchangeRate } from "stores/exchangeRates"
import FlexibleContent from "components/FlexibleContent/FlexibleContent"
import { useInterfaceStatuses } from "stores/interfaceStatuses"
import SendInvoiceToErpRadio from "components/SendInvoiceToErpRadio/SendInvoiceToErpRadio"

export enum PartialNoteType {
    credit = OutgoingInvoiceType.partialCreditNote,
    debit = OutgoingInvoiceType.partialDebitNote,
}

interface IPartialNoteCreateModalProps {
    type: PartialNoteType
    invoiceId: Guid
    onCreated?(): Promise<void>
    onClose(): void
    onCustomerBlocked(): void
    onInvoiceBlocked(): void
}

export type PartialNoteAmounts = { [key: string]: { inputValue: string, amount: number | null } }

const PartialNoteCreateModal: FC<IPartialNoteCreateModalProps> = ({
    type,
    invoiceId,
    onCreated,
    onClose,
    onCustomerBlocked,
    onInvoiceBlocked,
}) => {
    const { t } = useTranslation()
    const { isInvoiceERPInterfaceConnected } = useInterfaceStatuses()
    const { invoice } = useOutgoingInvoice({
        invoiceId,
        onFail: () => {
            toastMessages.showSystemError()
            onClose()
        }
    })

    const tenantCurrency = useTenantCurrency()
    const currency = invoice?.customer.currency || tenantCurrency
    const today = useMemo(() => dayjs().startOf("day"), [])
    const exchangeRate = useExchangeRate({ currency, date: today })

    const isCredit = type === PartialNoteType.credit
    const isDebit = type === PartialNoteType.debit
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [headerText, setHeaderText] = useState("")
    const [footerText, setFooterText] = useState("")
    const [sendToErp, setSendToErp] = useState(isInvoiceERPInterfaceConnected)
    const [internalComment, setInternalComment] = useState("")
    const [amounts, setAmounts] = useState<PartialNoteAmounts>({})
    const hasNoZeroNumericCreditedAmount = useMemo(() => {
        return Object.values(amounts).some(({ amount }) => typeof amount === "number" && amount !== 0)
    }, [amounts])
    const [invoicePositions, setInvoicePositions] = useState<ReservationInvoiceInvoicePosition[] | null>(invoice?.invoicePositions || null)
    const totalNoteAmount = useMemo(() => {
        return Object
            .values(amounts)
            .reduce((acc, { amount }) => acc + (amount ?? 0), 0)
    }, [amounts])
    const totalCreditNoteAmountError = useMemo(() => {
        if (!invoice) return

        if (!hasNoZeroNumericCreditedAmount && totalNoteAmount === 0) {
            return t("validation.required")
        }

        if (isCredit) {
            if (
                Math.abs(totalNoteAmount) > Math.abs(invoice.residualAmount)
                    || invoice.residualAmount < 0 ? totalNoteAmount > 0 : totalNoteAmount < 0
            ) return t("noteCreate.creditNoteAmountExceedInvoiceAmount")
        } else if (isDebit) {
            if (
                Math.abs(totalNoteAmount) > Math.abs(invoice.creditedAmount)
                    || invoice.creditedAmount < 0 ? totalNoteAmount > 0 : totalNoteAmount < 0
            ) return t("noteCreate.debitNoteAmountExceedCreditedAmount")
        }
    }, [invoice, hasNoZeroNumericCreditedAmount, totalNoteAmount, isCredit, isDebit, t])

    const invoicePositionsValidationErrors = useMemo((): { [x: string]: CustomError } => {
        const validationFields: [string, IValidationRule, any][] = []

        for (const invoicePositionId of Object.keys(amounts)) {
            const invoicePosition = invoicePositions?.find(ip => ip.id === invoicePositionId)
            let additionalValidationRules: IValidationRule = {}

            if (isCredit) {
                const currentAmount = invoicePosition?.residualAmount ?? null
                if (currentAmount !== null) {
                    if (currentAmount > 0) {
                        additionalValidationRules = {
                            min: 0,
                            max: currentAmount,
                        }
                    } else if (currentAmount < 0) {
                        additionalValidationRules = {
                            min: currentAmount,
                            max: 0,
                        }
                    } else {
                        additionalValidationRules = {
                            min: 0,
                            max: 0,
                        }
                    }
                }
            } else if (isDebit) {
                const creditedAmount = invoicePosition?.creditedAmount ?? null
                if (creditedAmount !== null) {
                    if (creditedAmount > 0) {
                        additionalValidationRules = {
                            min: 0,
                            max: creditedAmount,
                        }
                    } else if (creditedAmount < 0) {
                        additionalValidationRules = {
                            min: creditedAmount,
                            max: 0,
                        }
                    } else {
                        additionalValidationRules = {
                            min: 0,
                            max: 0,
                        }
                    }
                }
            }

            validationFields.push([invoicePositionId, {
                type: "decimal",
                ...additionalValidationRules,
            }, amounts[invoicePositionId]?.inputValue])
        }

        return ValidationService.validateMultiple(validationFields)
    }, [amounts, invoicePositions, isCredit, isDebit])

    useEffect(() => {
        (async () => {
            if (!invoice?.invoicePositions) return
            const newInvoicePositions = await hydrateInvoicePositions(invoice?.invoicePositions)
            setInvoicePositions(newInvoicePositions)
        })()
    }, [invoice?.invoicePositions])

    const handleAmountChange = useCallback((params: { id: Guid, inputValue: string, amount: number | null }) => {
        setAmounts(prev => ({
            ...prev,
            [params.id]: {
                inputValue: params.inputValue,
                amount: params.amount,
            }
        }))
    }, [])

    async function handleCreatePartialNote() {
        setIsSubmitted(true)

        if (totalCreditNoteAmountError
            || Object.keys(invoicePositionsValidationErrors).length > 0) return

        setIsSubmitting(true)

        const link = type === PartialNoteType.credit
            ? invoice?.linkAddCreditNote
            : invoice?.linkAddDebitNote

        if (!link) {
            console.log("No link to create partial note")
            toastMessages.showSystemError()
            return
        }
        try {
            await AssetService.createPartialNote(link, {
                headerText,
                footerText,
                internalComment,
                sendToErp: isInvoiceERPInterfaceConnected ? sendToErp : undefined,
                items: Object.entries(amounts)
                    .filter(([_, { amount }]) => amount !== null)
                    .map(([id, { amount }]) => ({
                        invoicePositionId: id,
                        amount: typeof amount === "number" && amount !== 0 ? (amount * (isCredit ? -1 : 1)) : 0, // reverse sign for backend for credit
                    })),
            })
            onClose()
        } catch (e) {
            await handleCreditDebitNoteCreateError({
                e,
                closeCreateModal: onClose,
                onCustomerBlocked,
                onInvoiceBlocked,
            })
            setIsSubmitting(false)
            return
        }

        if (onCreated) {
            try {
                await onCreated()
            } catch (e) {
                toastMessages.showSystemError()
            }
        }

        setIsSubmitting(false)
    }

    return (
        <ConfirmModal
            title={t(isDebit ? 'noteCreate.createDebitNote' : 'noteCreate.createCreditNote')}
            maxWidth="952px"
            confirmButtonLabel={t(isDebit ? 'outgoingInvoiceControls.createDebitNote' : 'outgoingInvoiceControls.createCreditNote')}
            onConfirm={handleCreatePartialNote}
            onClose={onClose}
            isBusy={!invoice || isSubmitting}>
            {invoice
                ? <>
                    <div className="mb-4">
                        <Grid columns="minmax(0, auto) repeat(3, 18.8rem)"
                            gap="3.2rem">
                            <div>
                                <FlexibleContent text={t('noteCreate.originalInvoice')} />
                                <div className="mt-2">
                                    <ErpInvoiceNumber
                                        invoiceNumber={invoice.erpInvoiceNumber}
                                        invoiceNumberStatus={invoice.erpInvoiceNumberStatus}
                                        invoiceDate={invoice.erpInvoiceDate}
                                        isBold
                                    />
                                </div>
                            </div>
                            <div className="text-right">
                                <FlexibleContent text={t('noteCreate.originalInvoicedAmount')} />
                                <div className="font-bold mt-2">
                                    {t('common.amountWithCurrency', { count: invoice.amount, currency })}
                                </div>
                            </div>
                            <div className="text-right">
                                <FlexibleContent text={t('noteCreate.currentInvoicedAmount')} />
                                <div className="font-bold mt-2">
                                    {t('common.amountWithCurrency', { count: invoice.residualAmount, currency })}
                                </div>
                            </div>
                            <div className="text-right">
                                <FlexibleContent text={t(isDebit ? 'noteCreate.debitNoteAmount' : 'noteCreate.creditNoteAmount')} />
                                <div className={cn("font-bold mt-2", { "color-red-500": isCredit })}>
                                    {t('common.amountWithCurrency', { count: totalNoteAmount, currency })}
                                </div>
                                {!!exchangeRate && !!tenantCurrency &&
                                    <Text type="pSmallest" className="color-grey-500">
                                        {t('common.amountWithCurrency', { count: applyExchangeRate(totalNoteAmount, exchangeRate), currency: tenantCurrency })}
                                    </Text>
                                }
                            </div>
                        </Grid>
                        {isSubmitted && !!totalCreditNoteAmountError && Object.keys(invoicePositionsValidationErrors).length === 0 &&
                            <div className="text-right">
                                <ValidationMessage>{totalCreditNoteAmountError}</ValidationMessage>
                            </div>
                        }
                    </div>
                    {invoicePositions?.map(ip => (
                        <div key={ip.id}
                            className="border-grey-100-between py-1">
                            <PartialNoteInvoicePosition
                                partialNoteType={type}
                                invoicePosition={ip}
                                currency={currency}
                                exchangeRate={exchangeRate}
                                onAmountChange={handleAmountChange}
                                validationError={invoicePositionsValidationErrors[ip.id]}
                            />
                        </div>
                    ))}
                    <div className="mt-4">
                        <TextSection
                            title={t('noteCreate.headerSection')}
                            popupTitle={t(`outgoingInvoiceControls.${isDebit ? 'headerTextEditTitleDebitNote' : 'headerTextEditTitleCreditNote'}`)}
                            text={headerText}
                            onChange={setHeaderText}
                            formattingRuleLabel={t("textModal.lineBreakAfter", { lineBreakAfter: PARTIAL_CREDIT_NOTE_TEXT_LINE_BREAK_AFTER })}
                            lineBreakAfter={PARTIAL_CREDIT_NOTE_TEXT_LINE_BREAK_AFTER}
                            textVariables
                            monospace
                        />
                    </div>
                    <div className="mt-4">
                        <TextSection
                            title={t('noteCreate.footerSection')}
                            popupTitle={t(`outgoingInvoiceControls.${isDebit ? 'footerTextEditTitleDebitNote' : 'footerTextEditTitleCreditNote'}`)}
                            text={footerText}
                            onChange={setFooterText}
                            formattingRuleLabel={t("textModal.lineBreakAfter", { lineBreakAfter: PARTIAL_CREDIT_NOTE_TEXT_LINE_BREAK_AFTER })}
                            lineBreakAfter={PARTIAL_CREDIT_NOTE_TEXT_LINE_BREAK_AFTER}
                            textVariables
                            monospace
                        />
                    </div>
                    {isInvoiceERPInterfaceConnected &&
                        <div className="mt-4">
                            <SendInvoiceToErpRadio
                                sendToErp={sendToErp}
                                isZeroAmount={totalNoteAmount === 0}
                                onChange={setSendToErp}
                            />
                        </div>
                    }
                    <FormField
                        className="mt-4"
                        label={t('noteCreate.internalComments')}>
                        <Input
                            type="textarea"
                            rows={7}
                            value={internalComment}
                            onChange={e => setInternalComment(e.target.value)}
                            theme="default"
                            maxLength={INPUT_MAX_LENGTH_LARGE}
                        />
                    </FormField>
                </>
                : <Spinner className="py-3" />
            }
        </ConfirmModal>
    )
}

export default PartialNoteCreateModal
